import { createSelector } from 'reselect';
import { List, Map, Set } from 'immutable';
import get from 'lodash/get';

import Pricing from 'models/pricing';
import { AddOns } from 'models/addons';
import { Events } from 'models/event';

const readProp = (slice, key, defaultValue) => {
  if (!slice) {
    return defaultValue;
  }
  if (typeof slice.get === 'function') {
    const val = slice.get(key);
    return val !== undefined ? val : defaultValue;
  }
  return get(slice, key, defaultValue);
};

export const getCheckoutState = state => get(state, 'checkout', null);

export const getCheckout = getCheckoutState;

export const getCheckoutInitialized = createSelector(
  [getCheckoutState],
  checkout => Boolean(readProp(checkout, 'initialized', false)),
);

export const getCheckoutSubmitted = createSelector(
  [getCheckoutState],
  checkout => Boolean(readProp(checkout, 'submitted', false)),
);

export const getCheckoutPricing = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'pricing', new Pricing()),
);

export const getCheckoutSubTotal = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'subTotal', null),
);

export const getCheckoutTotal = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'checkoutTotal', null),
);

export const getCouponCode = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'couponCode', null),
);

export const getCouponAmount = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'couponAmount', null),
);

export const getAutoApplyCoupon = createSelector(
  [getCheckoutState],
  checkout => Boolean(readProp(checkout, 'autoApplyCoupon', true)),
);

export const getUserCreditAmount = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'userCreditAmount', null),
);

export const getSelectedAddOns = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'selectedAddOns', new AddOns()),
);

export const getCheckoutFees = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'fees', List()),
);

export const getBookExtendedTimes = createSelector(
  [getCheckoutState],
  checkout => Boolean(readProp(checkout, 'bookExtendedTimes', true)),
);

export const getSaveCreditCard = createSelector(
  [getCheckoutState],
  checkout => Boolean(readProp(checkout, 'saveCreditCard', true)),
);

export const getIsPaypal = createSelector(
  [getCheckoutState],
  checkout => Boolean(readProp(checkout, 'isPaypal', false)),
);

export const getCheckoutError = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'error', Map()),
);

export const getCheckoutApiError = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'apiError', Map()),
);

export const getCheckoutRequiredFields = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'requiredFields', Set()),
);

export const getCheckoutRequiredFieldErrors = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'requiredFieldErrors', Map()),
);

export const getRequiredFieldErrors = getCheckoutRequiredFieldErrors;

export const getCheckoutShouldScrollToError = createSelector(
  [getCheckoutState],
  checkout => Boolean(readProp(checkout, 'shouldScrollToError', false)),
);

export const getPlateNumber = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'plateNumber', null),
);

export const getCheckoutEvents = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'events', Events()),
);

export const getCheckoutVenueName = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'venueName', null),
);

export const getPortalAffiliateId = createSelector(
  [getCheckoutState],
  checkout => readProp(checkout, 'portalAffiliateId', null),
);
